import { inject, Injectable } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { omit, values } from 'lodash';
import { lastValueFrom } from 'rxjs';
import { AnswersByName } from '../../../team-apis/otterdam/answers/answer';
import { Task } from '../../../team-apis/otterdam/task';
import {
  TrainingTemplateResponse,
  TrainingTemplateResponseObjectless,
} from '../../../team-apis/otterdam/training-template';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { getAnnotationAnswerObjectless } from '../../outputs/annotation/annotation-shared/shape-layer-answer-helper';
import { TimerService } from '../../production-workspace/timer-badge/timer.service';
import { trainingTemplatePath } from '../../shared/misc-services/training-template-resource.service';
import { AbstractResource } from '../abstract-resource';
import { initializeAnnotations } from './shape-initializer-utils';

@Injectable()
export class TemplateLoadingService extends AbstractResource {
  private store = inject(TaskWorkspaceStore);
  private timerService = inject(TimerService);

  async load(templateId: string): Promise<Task> {
    if (!this.store.isUsingObjects()) {
      return this.loadObjectless(templateId);
    }

    const response = await lastValueFrom(
      this.request$<TrainingTemplateResponse>(
        'GET',
        trainingTemplatePath(this.workspaceConfig.projectId, templateId),
        {
          params: { forWorkspace: 'true' },
          tokenScope: 'access_token',
          customErrorMessage: 'failed to load template.',
        },
      ),
    );

    initializeAnnotations(this.store, this.timerService, response.answers);

    this.store('trainingTemplate').set(
      omit(response, 'data', 'signed_urls', 'answers'),
    );

    return {
      id: encodeURIComponent(`training_template#${templateId}`),
      next_internal_id: 1,
      round: 1,
      data: response.data,
      answers_or_gold: {},
      default_answers: {},
      assets_archived_at: null,
      _extras: { signed_urls: response.signed_urls },
    };
  }

  async loadObjectless(templateId: string): Promise<Task> {
    const { projectId } = this.workspaceConfig;

    const response = await lastValueFrom(
      this.request$<TrainingTemplateResponseObjectless>(
        'GET',
        trainingTemplatePath(projectId, templateId),
        {
          params: { forWorkspace: 'true' },
          tokenScope: 'access_token',
          customErrorMessage: 'failed to load template.',
        },
      ),
    );

    this.store('trainingTemplate').set(
      omit(response, 'data', 'signed_urls', 'answers'),
    );

    return {
      id: encodeURIComponent(`training_template#${templateId}`),
      next_internal_id: this.getMaxShapeIdFromAnswers(response.answers) + 1,
      round: 1,
      data: response.data,
      answers_or_gold: response.answers,
      default_answers: {},
      assets_archived_at: null,
      _extras: { signed_urls: response.signed_urls },
    };
  }

  private getMaxShapeIdFromAnswers(answers: AnswersByName) {
    const indexes = values(answers)
      .map(getAnnotationAnswerObjectless)
      .filter(isDefined)
      .flatMap((answer) => answer.shape_groups)
      .flatMap((shapeGroups) => shapeGroups.shapes)
      .map((shape) => Number(shape.index));

    if (indexes.length === 0) {
      return 0;
    }

    return Math.max(...indexes);
  }
}
